import { NodeType } from "@features/builder/graph/types"
import { MenuItem, TextField } from "@mui/material"
import { Control, Controller, FieldValues, Path, useFormContext } from "react-hook-form"
import { useState } from "react"
import ConfirmDialog from "@components/ConfirmDialog"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import NodeTypeChip from "@components/NodeTypeChip"

type NodeTypeFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  disabled?: boolean
  isOptionDisabled?: (type: NodeType) => boolean
  isOptionPresent?: (type: NodeType) => boolean
  onChange?: (type: NodeType) => void
}

type PendingChange = {
  newValue: NodeType
  onChange: (value: NodeType) => void
}

const NodeTypeField = <TFieldValues extends FieldValues>(
  props: NodeTypeFieldProps<TFieldValues>,
) => {
  const { getValues, setValue } = useFormContext<NodeFormValues>()

  // store node type change if user needs to confirm it (and apply if confirmed)
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null)

  const allNodeTypes = Object.values(NodeType)
  const nodeTypes = props.isOptionPresent
    ? allNodeTypes.filter(props.isOptionPresent)
    : allNodeTypes

  const handleConfirmClose = (confirmed: boolean) => {
    if (confirmed && pendingChange) {
      setValue("link", "", { shouldDirty: true, shouldValidate: true })
      pendingChange.onChange(pendingChange.newValue)
      props.onChange?.(pendingChange.newValue)
    }
    setPendingChange(null)
  }

  return (
    <>
      <Controller
        control={props.control}
        name={props.name}
        render={({ field: { onChange, ...field }, fieldState }) => (
          <TextField
            select
            size="small"
            label="Type"
            fullWidth
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            sx={{ textTransform: "capitalize" }}
            disabled={props.disabled}
            onChange={({ target: { value: newValue } }) => {
              const hasLink = getValues("link")

              if (hasLink) {
                setPendingChange({ newValue: newValue as NodeType, onChange })
              } else {
                onChange(newValue)
                props.onChange?.(newValue as NodeType)
              }
            }}
            {...field}
          >
            {nodeTypes.map((type) => (
              <MenuItem key={type} value={type} disabled={props.isOptionDisabled?.(type)}>
                <NodeTypeChip type={type} />
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <ConfirmDialog
        open={Boolean(pendingChange)}
        dialogTitle="Confirm node type change"
        onClose={handleConfirmClose}
      >
        This node is linked to another <strong>{getValues("type").toUpperCase()}</strong> node.
        Linked nodes must have the same type.
        <br /> <br />
        Changing to <strong>{pendingChange?.newValue.toUpperCase()}</strong> will remove this link.
        <br />
        Continue?
      </ConfirmDialog>
    </>
  )
}

export default NodeTypeField
